import { createApi } from '@reduxjs/toolkit/query/react';
import type { AdvisorGroupDisclaimer } from '@/models/AppViewModel';
import { waServicesQuery } from './baseQueries';

/** Platform GET response: list of disclaimers for an advisor group (typically one record). */
export interface GetDisclaimersResponse {
  Records?: AdvisorGroupDisclaimer[];
}

/** If the server returns 404, the route may not be registered or URL casing may differ (e.g. /api/Firm/...). */
export const disclaimersApi = createApi({
  reducerPath: 'disclaimersApi',
  baseQuery: waServicesQuery,
  tagTypes: ['Disclaimers'],
  endpoints: (builder) => ({
    getDisclaimers: builder.query<AdvisorGroupDisclaimer[], number>({
      query: (advisorGroupId) => ({
        url: `/api/firm/advisorGroups/${advisorGroupId}/disclaimers`,
        method: 'GET',
      }),
      transformResponse: (
        raw: GetDisclaimersResponse,
      ): AdvisorGroupDisclaimer[] => {
        const records = raw?.Records;
        return Array.isArray(records) ? records : [];
      },
      providesTags: (_result, _err, advisorGroupId) => [
        { type: 'Disclaimers', id: String(advisorGroupId) },
      ],
    }),
    updateDisclaimer: builder.mutation<
      boolean,
      { advisorGroupId: number; disclaimer: AdvisorGroupDisclaimer }
    >({
      query: ({ advisorGroupId, disclaimer }) => ({
        url: `/api/firm/advisorGroups/${advisorGroupId}/disclaimers/${disclaimer.AdvisorGroupDisclaimerId}`,
        method: 'PUT',
        body: disclaimer,
      }),
      invalidatesTags: (_result, _err, { advisorGroupId }) => [
        { type: 'Disclaimers', id: String(advisorGroupId) },
      ],
    }),
    createDisclaimer: builder.mutation<
      AdvisorGroupDisclaimer,
      {
        advisorGroupId: number;
        disclaimer: Omit<AdvisorGroupDisclaimer, 'AdvisorGroupDisclaimerId'>;
      }
    >({
      query: ({ advisorGroupId, disclaimer }) => ({
        url: `/api/firm/advisorGroups/${advisorGroupId}/disclaimers`,
        method: 'POST',
        body: { ...disclaimer, AdvisorGroupId: advisorGroupId },
      }),
      invalidatesTags: (_result, _err, { advisorGroupId }) => [
        { type: 'Disclaimers', id: String(advisorGroupId) },
      ],
    }),
    deleteDisclaimer: builder.mutation<
      void,
      { advisorGroupId: number; advisorGroupDisclaimerId: number }
    >({
      query: ({ advisorGroupId, advisorGroupDisclaimerId }) => ({
        url: `/api/firm/advisorGroups/${advisorGroupId}/disclaimers/${advisorGroupDisclaimerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, { advisorGroupId }) => [
        { type: 'Disclaimers', id: String(advisorGroupId) },
      ],
    }),
  }),
});

export const {
  useGetDisclaimersQuery,
  useUpdateDisclaimerMutation,
  useCreateDisclaimerMutation,
  useDeleteDisclaimerMutation,
} = disclaimersApi;
